import { EventCustomAttributes } from 'utils/analytics/eventTypeConstantsAmplitude';
import { Typography } from 'omakase';
import {
  accessKeyAwareOnClickWrapper,
  markStandardKeyboardClickIntent,
} from 'utils/accessKeyUtils';
import { consumer } from '@tock/coretypes';
import Anchor from 'components/Anchor';

type ClickContext = EventCustomAttributes['clickContext'];

export interface OfferingHeadingProps {
  isShowingOnBusinessPage?: boolean;
  navigate: boolean;
  linkTo?: string;
  label?: string;
  offering: consumer.Offering;
  onNavigateToExperience?(event: consumer.Offering, nextAvailableDate?: string): void;
  onClick?(): void;
  onClickTarget: (context: ClickContext) => void;
  dataTestIdValue: string;
  includeAccessKey: boolean;
}

const RELEVANT_ACCESS_KEY = 'b';

const OfferingHeadingTypography: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    className="Consumer-reservationHeading"
    component="h3"
    variant="heading03"
    data-testid="experience-name"
  >
    {children}
  </Typography>
);

const OfferingHeading: React.FC<OfferingHeadingProps> = ({
  isShowingOnBusinessPage,
  navigate,
  linkTo,
  label,
  offering,
  onNavigateToExperience,
  onClick,
  onClickTarget,
  dataTestIdValue,
  includeAccessKey,
}) => {
  const { name } = offering;

  const opensModal = navigate && !linkTo;
  const isStatic = !navigate && !linkTo;
  const isLink = !navigate && linkTo;
  const accessKey = includeAccessKey ? RELEVANT_ACCESS_KEY : undefined;

  return isShowingOnBusinessPage ? (
    <>
      {opensModal && (
        <button
          aria-label={label}
          onClick={(event) => {
            accessKeyAwareOnClickWrapper({
              event,
              onStandardClick: () => {
                onNavigateToExperience?.(offering);
                onClickTarget('Title');
              },
            });
          }}
          onKeyDown={markStandardKeyboardClickIntent}
          className="Consumer-reservationLink"
          accessKey={accessKey}
        >
          <OfferingHeadingTypography>{name}</OfferingHeadingTypography>
        </button>
      )}
      {isStatic && <OfferingHeadingTypography>{name}</OfferingHeadingTypography>}
      {isLink && (
        <OfferingHeadingTypography>
          <Anchor
            data-testid={`offering-link_${dataTestIdValue}`}
            onClick={(event) => {
              accessKeyAwareOnClickWrapper({
                event,
                onStandardClick: () => {
                  onClick?.();
                  onClickTarget('Title');
                },
              });
            }}
            onKeyDown={markStandardKeyboardClickIntent}
            aria-label={label}
            className="Consumer-reservationLink"
            href={linkTo}
            accessKey={accessKey}
          >
            {name}
          </Anchor>
        </OfferingHeadingTypography>
      )}
    </>
  ) : (
    <>
      {navigate && (
        <button
          aria-label={`View ${name} (opens a dialog)`}
          onClick={(event) => {
            accessKeyAwareOnClickWrapper({
              event,
              onStandardClick: () => {
                onNavigateToExperience?.(offering);
              },
            });
          }}
          onKeyDown={markStandardKeyboardClickIntent}
          className="Consumer-reservationLink"
          accessKey={accessKey}
        >
          <OfferingHeadingTypography>{name}</OfferingHeadingTypography>
        </button>
      )}
      {!navigate && <OfferingHeadingTypography>{name}</OfferingHeadingTypography>}
    </>
  );
};

export default OfferingHeading;
